import React, { useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react/dist/ckeditor';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { registAuthorNote } from '../../../api';

// Custom Upload Adapter
class MyUploadAdapter {
    loader: any;
    editor: any;

    constructor(loader: any, editor: any) {
        this.loader = loader;
        this.editor = editor;
    }

    upload() {
        return this.loader.file.then((file: any) => new Promise((resolve, reject) => {
            // Check image count
            const data = this.editor.getData();
            const imageCount = (data.match(/<img/g) || []).length;

            if (imageCount >= 3) {
                alert("이미지는 최대 3개까지만 등록 가능합니다.");
                reject("Image limit exceeded");
                return;
            }

            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event: any) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const MAX_WIDTH = 800; // DB TEXT limit safe size
                    let width = img.width;
                    let height = img.height;

                    // Maintain aspect ratio
                    if (width > MAX_WIDTH) {
                        height *= MAX_WIDTH / width;
                        width = MAX_WIDTH;
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx?.drawImage(img, 0, 0, width, height);

                    // Compress to JPEG to reduce size
                    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
                    resolve({ default: dataUrl });
                };
                img.onerror = (error) => reject(error);
            };
            reader.onerror = (error) => reject(error);
        }));
    }
    abort() { }
}

function MyCustomUploadAdapterPlugin(editor: any) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => {
        return new MyUploadAdapter(loader, editor);
    };
}

interface AuthorNoteWriteProps {
    authorEmail: string;
    initialData?: any;
    onCancel: () => void;
    onComplete: () => void;
}

const AuthorNoteWrite = ({ authorEmail, initialData, onCancel, onComplete }: AuthorNoteWriteProps) => {
    const [title, setTitle] = useState(initialData?.title || '');
    const [content, setContent] = useState(initialData?.content || '');
    const [category, setCategory] = useState(initialData?.category || '영감 노트');

    const categories = ['영감 노트', '창작 노트', '작품 비하인드', '자유 글'];

    const handleSubmit = async () => {
        if (!title.trim() || !content.trim()) {
            alert("제목과 내용을 입력해주세요.");
            return;
        }

        const noteId = initialData?.noteId || initialData?.id || "";
        const formData = new FormData();
        formData.append("authorEmail", authorEmail);
        formData.append("category", category);
        formData.append("title", title);
        formData.append("content", content);
        formData.append("noteId", noteId);

        // 썸네일 추출 및 추가 (첫 번째 이미지)
        const imgMatch = content.match(/<img[^>]+src="([^">]+)"/);

        if (imgMatch && imgMatch[1]) {
            const imgSrc = imgMatch[1];
            try {
                let blob: Blob | null = null;

                if (imgSrc.startsWith('data:')) {
                    // Base64 이미지인 경우
                    blob = dataURLtoBlob(imgSrc);
                } else {
                    // URL 이미지인 경우 (기존 업로드된 이미지) -> Fetch하여 Blob으로 변환
                    // 자신의 서버 이미지라고 가정하고 fetch 시도
                    const response = await fetch(imgSrc);
                    if (response.ok) {
                        blob = await response.blob();
                    }
                }

                // 백엔드 cwebp 에러 회피를 위해 thumbnail 파일을 직접 전송하지 않고 본문 이미지로 우회
                // if (blob) {
                //     formData.append('thumbnail', blob, 'thumbnail.jpg');
                // }
            } catch (e) {
                console.error("Thumbnail extraction failed", e);
            }
        } else {
            // 이미지가 없는 경우 (썸네일 삭제)
            // 백엔드가 빈 파일을 받아서 삭제 처리하는지, 아니면 필드 부재로 처리하는지 확실치 않음.
            // 다만, 여기서는 썸네일을 보내지 않음으로써 삭제 의도를 전달.
            // 필요하다면 빈 Blob을 보낼 수도 있음: formData.append('thumbnail', new Blob([]), '');
        }

        try {
            await registAuthorNote(formData);
            alert(initialData ? "수정되었습니다." : "등록되었습니다.");
            onComplete();
        } catch (error) {
            console.error("Error saving note:", error);
            alert("저장에 실패했습니다.");
        }
    };

    // Data URL to Blob 변환 헬퍼 함수
    const dataURLtoBlob = (dataurl: string) => {
        const arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)?.[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new Blob([u8arr], { type: mime });
    }

    return (
        <div className="w-full bg-[#f5f5f5] p-4 md:p-8 flex flex-col gap-4 md:gap-5">
            {/* 카테고리 선택 */}
            <div className="flex gap-2 overflow-x-auto whitespace-nowrap pb-1 scrollbar-none">
                {categories.map((cat) => (
                    <button
                        key={cat}
                        onClick={() => setCategory(cat)}
                        className={`h-[34px] md:h-[38px] px-3 md:px-4 rounded-full text-[13px] md:text-[16px] font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                            category === cat
                                ? 'bg-[#19cf7a] text-white'
                                : 'bg-white text-[#4b5563] border border-[#d1d5db] hover:bg-gray-50'
                        }`}
                    >
                        {cat}
                    </button>
                ))}
            </div>

            {/* 카테고리 안내문 */}
            <p className="text-[#6b7280] text-[13px] md:text-[16px] font-normal leading-relaxed">
                작품을 기획하는 과정에서 영감을 받았던 순간을 자유롭게 표현해 보세요! 카페, 집 등 일상 속에서 발견한 영감의 순간들을 독자들과 공유할 수 있어요.
            </p>

            {/* 에디터 카드 */}
            <div className="w-full bg-white rounded-[8px] border border-[#e5e7eb] shadow-[0px_1px_1px_rgba(0,0,0,0.05)] overflow-hidden flex flex-col min-h-[400px] md:min-h-[600px]">
                {/* 제목 입력 */}
                <div className="px-4 pt-4 pb-3 md:px-6 md:pt-5 md:pb-4">
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="제목을 입력하세요"
                        className="w-full text-[18px] md:text-[24px] placeholder-[#adaebc] border-none outline-none focus:ring-0 p-0 text-[#1f2937] bg-transparent"
                    />
                </div>

                <div className="h-[1px] bg-[#e5e7eb] mx-0 mb-4" />

                {/* CKEditor */}
                <div className="flex-1 px-4">
                    <CKEditor
                        editor={ClassicEditor as any}
                        data={content}
                        onChange={(event: any, editor: any) => {
                            const data = editor.getData();
                            setContent(data);
                        }}
                        config={{
                            placeholder: "작가님의 이야기를 자유롭게 작성해보세요.",
                            extraPlugins: [MyCustomUploadAdapterPlugin],
                            toolbar: ['heading', '|', 'bold', 'italic', 'underline', 'strikethrough', 'blockQuote', '|', 'bulletedList', 'numberedList', '|', 'alignment', 'link', 'imageUpload', '|', 'undo', 'redo'],
                        }}
                    />
                </div>
            </div>

            {/* 하단 액션 */}
            <div className="flex justify-end items-center gap-3 pb-2 md:pb-4">
                <button
                    onClick={onCancel}
                    className="px-4 py-2 md:px-6 md:py-2.5 text-[#6b7280] text-[14px] md:text-[16px] font-medium hover:text-[#1f2937] transition-colors"
                >
                    취소
                </button>
                <button
                    onClick={handleSubmit}
                    className="flex items-center justify-center px-6 py-2.5 md:px-[38px] md:py-[13px] bg-[#19cf7a] text-white text-[14px] md:text-[16px] font-bold rounded-[30px] hover:bg-[#00b368] transition-colors"
                >
                    등록
                </button>
            </div>

            <style>{`
                .ck.ck-editor__main > .ck-editor__editable {
                    min-height: 450px;
                    border: none !important;
                    box-shadow: none !important;
                    padding: 0 16px !important;
                }
                .ck.ck-toolbar {
                    border: none !important;
                    background: transparent !important;
                    padding: 0 0 20px 0 !important;
                }
                .ck-content {
                    font-family: 'Pretendard', sans-serif;
                    font-size: 16px;
                    line-height: 1.8;
                }
            `}</style>
        </div>
    );
};

export default AuthorNoteWrite;